import { ArrowRight, CheckCircle2, Circle, Clock, ExternalLink, XCircle } from "lucide-react";
import { SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import { isLitigeRowOpen } from "@/lib/buyerPrivateAccess";
import { cn } from "@/lib/utils";

function formatDt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("fr-FR");
}

function formatEur(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toLocaleString("fr-FR")} €`;
}

function stripeSearchUrl(id) {
  if (!id) return null;
  return `https://dashboard.stripe.com/search?query=${encodeURIComponent(String(id))}`;
}

function stripeTransferUrl(transferId) {
  if (!transferId) return null;
  return `https://dashboard.stripe.com/connect/transfers/${encodeURIComponent(String(transferId))}`;
}

function stepIcon(status) {
  if (status === "done") return <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" aria-hidden />;
  if (status === "blocked") return <XCircle className="h-5 w-5 text-red-600 shrink-0" aria-hidden />;
  if (status === "wait") return <Clock className="h-5 w-5 text-amber-600 shrink-0" aria-hidden />;
  return <Circle className="h-5 w-5 text-slate-300 shrink-0" aria-hidden />;
}

/**
 * Fil d’argent admin : acheteur → plateforme → vendeur (dates, montants, liens Stripe).
 */
export default function AdminMoneyTrail({ tx }) {
  if (!tx) return null;

  const buyerTotal = Number(tx.buyer_total ?? tx.price);
  const price = Number(tx.price);
  const commission = Number(tx.commission ?? 0);
  const sellerNet =
    tx.seller_net != null && Number.isFinite(Number(tx.seller_net))
      ? Number(tx.seller_net)
      : Math.max(0, price - (Number.isFinite(commission) ? commission : 0));

  const statut = String(tx.statut || "").toLowerCase();
  const rembourse = statut === "rembourse" || statut === "annule";
  const rdvOk = Boolean(tx.capture_confirmed_at) || statut === "fonds_debloques";
  const transferred = Boolean(tx.stripe_connect_transfer_id);
  const onHold = Boolean(tx.seller_payout_on_hold);
  const connectOk =
    tx.seller?.stripe_connect_ready_live === true ||
    tx.seller?.stripe_onboarding_complete === true ||
    tx.seller?.seller_had_successful_payout === true;
  const payoutAt = tx.seller_payout_available_at ? Date.parse(String(tx.seller_payout_available_at)) : NaN;
  const payoutDue = Number.isFinite(payoutAt) && payoutAt <= Date.now();

  const openLitige = (tx.litiges || []).some(isLitigeRowOpen);

  const steps = [];

  const buyerPaid =
    rdvOk || ["paye", "appel_en_cours", "appel_effectue", "fonds_debloques", "litige"].includes(statut);

  steps.push({
    status: rembourse ? "blocked" : buyerPaid ? "done" : "wait",
    title: "1. Acheteur paie (carte)",
    lines: [
      `${tx.buyer?.name || "Acheteur"} · ${formatEur(buyerTotal)}`,
      buyerPaid
        ? `Paiement validé · ${formatDt(tx.created_at)}`
        : statut === "en_attente" && tx.stripe_payment_intent_id
          ? `Checkout commencé le ${formatDt(tx.created_at)} — carte non finalisée`
          : `Commande créée le ${formatDt(tx.created_at)} — paiement non reçu`,
    ],
    stripe: tx.stripe_payment_intent_id,
    stripeLabel: "PaymentIntent Stripe",
  });

  steps.push({
    status: rembourse ? "blocked" : rdvOk ? "done" : "wait",
    title: "2. Plateforme RdvMarket",
    lines: [
      `Commission plateforme : ${formatEur(commission)}`,
      `Net vendeur (hors frais Stripe carte) : ${formatEur(sellerNet)}`,
      rdvOk
        ? `RDV confirmé le ${formatDt(tx.capture_confirmed_at)}`
        : "RDV pas encore confirmé — argent encore en séquestre",
      rembourse ? "Remboursement acheteur — rien pour le vendeur" : null,
    ].filter(Boolean),
    note: "Les ~0,25 € de frais Stripe sur la carte apparaissent dans Solde Stripe, pas ici.",
  });

  let vendeurStatus = "wait";
  if (rembourse) vendeurStatus = "blocked";
  else if (transferred) vendeurStatus = "done";
  else if (onHold || openLitige) vendeurStatus = "blocked";
  else if (rdvOk && payoutDue && connectOk) vendeurStatus = "wait";

  const vendeurLines = [
    `${tx.seller?.name || "Vendeur"} · ${tx.seller?.email || ""}`,
    connectOk ? "IBAN / Connect : OK" : "IBAN / Connect : incomplet — versement impossible",
  ];
  if (onHold || openLitige) {
    vendeurLines.push("Litige : versement gelé");
  } else if (rdvOk && tx.seller_payout_available_at) {
    vendeurLines.push(
      payoutDue
        ? `Versement autorisé depuis le ${formatDt(tx.seller_payout_available_at)} (délai ${SELLER_PAYOUT_HOURS} h)`
        : `Versement prévu le ${formatDt(tx.seller_payout_available_at)} (délai ${SELLER_PAYOUT_HOURS} h)`,
    );
  }
  if (transferred) {
    vendeurLines.push(`Versé le ${formatDt(tx.seller_payout_transferred_at)}`);
  } else if (tx.seller_payout_failed_at) {
    vendeurLines.push(`Échec : ${tx.seller_payout_failure_reason || "voir Stripe"}`);
  } else if (rdvOk && payoutDue && connectOk && !onHold) {
    vendeurLines.push("En file (cron) ou en attente de déclenchement admin");
  }

  steps.push({
    status: vendeurStatus,
    title: "3. Vendeur reçoit sur sa banque",
    lines: vendeurLines,
    stripe: tx.stripe_connect_transfer_id,
    stripeLabel: "Transfer Connect",
    stripeHref: stripeTransferUrl(tx.stripe_connect_transfer_id),
  });

  return (
    <div
      className="rounded-xl border-2 border-[#2D7FF9]/25 bg-gradient-to-b from-[#2D7FF9]/5 to-white p-4 sm:p-5"
      data-testid="admin-money-trail"
    >
      <p className="font-display text-base font-bold text-slate-900 mb-1">Où est l&apos;argent ? (cette vente)</p>
      <p className="text-xs text-slate-600 mb-4">
        Suivez <strong>ici</strong> acheteur → plateforme → vendeur. Dans Stripe « Solde », vous ne voyez que des lignes
        techniques (frais, remboursements) sans les noms.
      </p>

      <div className="space-y-3">
        {steps.map((step, i) => (
          <div key={step.title} className="flex gap-3">
            {stepIcon(step.status)}
            <div className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
              <p className="text-sm font-semibold text-slate-900">{step.title}</p>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
                {step.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {step.note ? <p className="mt-1.5 text-[10px] text-slate-500 italic">{step.note}</p> : null}
              {step.stripe ? (
                <a
                  href={step.stripeHref || stripeSearchUrl(step.stripe)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#2D7FF9] hover:underline"
                >
                  {step.stripeLabel || "Stripe"} <ExternalLink className="h-3 w-3" aria-hidden />
                </a>
              ) : null}
            </div>
            {i < steps.length - 1 ? (
              <ArrowRight className="hidden sm:block h-4 w-4 text-slate-300 shrink-0 self-center" aria-hidden />
            ) : null}
          </div>
        ))}
      </div>

      <p
        className={cn(
          "mt-4 rounded-lg px-3 py-2 text-xs font-medium",
          transferred
            ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
            : rembourse
              ? "bg-slate-100 text-slate-700 border border-slate-200"
              : onHold || openLitige
                ? "bg-orange-50 text-orange-900 border border-orange-200"
                : !connectOk && rdvOk
                  ? "bg-amber-50 text-amber-900 border border-amber-200"
                  : "bg-sky-50 text-sky-900 border border-sky-200",
        )}
      >
        {transferred
          ? "OK : l’argent a été transféré au compte Stripe du vendeur (puis sa banque selon Stripe)."
          : rembourse
            ? "Terminé : l’acheteur a été remboursé (ou paiement annulé)."
            : onHold || openLitige
              ? "Bloqué : litige — ne versez pas manuellement sans décision."
              : !connectOk && rdvOk
                ? "En attente : le vendeur doit compléter identité + IBAN (bouton en haut de son compte)."
                : !rdvOk
                  ? "En attente : RDV non confirmé — l’acheteur n’est pas encore débité définitivement."
                  : payoutDue && connectOk
                    ? "Prochaine étape : versement automatique (cron) ou bouton admin ci-dessous."
                    : `Prochaine étape : attendre la fin du délai ${SELLER_PAYOUT_HOURS} h après confirmation RDV.`}
      </p>
    </div>
  );
}
